// YTRepliesRateLimitsCard — dedicated per-channel rate-limits editor. Sits
// BELOW the console card at the bottom of Workbench (Channel 1) and Refinery
// (Channel 2). Each channel edits its own thresholds.
//
// Feb 2026 (Randy pt-yt-autosave) — was manual-save-only ("Save thresholds"
// button); every OTHER setting in the module auto-saves so this card was
// a stranding trap. Now auto-saves on blur (number input) / mouseup / touchend
// (slider) with an inline "Saved ✓" flash, mirroring the confidence gate
// threshold pattern already in SettingsTab. Save button removed.
import { useState, useEffect, useRef } from "react";
import { Gauge, ArrowClockwise, LockKey, YoutubeLogo, Info, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { SLOT_META } from "@/lib/ytRepliesSample";
import { useEntitlements } from "@/lib/entitlements";
import { ytApi } from "@/lib/ytApi";

// Defaults per Randy Jul 2026 (agent-suggested, Randy-approved):
//   max_posts_per_day = 30       (per channel)
//   max_posts_per_comment = 1    (never reply to the same comment twice)
//   min_spacing_seconds = 5      (5-second gap between consecutive posts)
const DEFAULTS = {
  maxPostsPerDay: 30,
  maxPostsPerComment: 1,
  minSpacingSeconds: 5,
};

// Sensible hard limits so nobody can put in something reckless before
// the backend enforces its own ceiling.
const BOUNDS = {
  maxPostsPerDay: { min: 1, max: 200, unit: "posts / day" },
  maxPostsPerComment: { min: 1, max: 3, unit: "posts / comment" },
  minSpacingSeconds: { min: 1, max: 300, unit: "seconds" },
};

// Map local camelCase state keys → backend snake_case field names.
const PATCH_KEYS = {
  maxPostsPerDay: "max_posts_per_day",
  maxPostsPerComment: "max_posts_per_comment",
  minSpacingSeconds: "min_spacing_seconds",
};

export default function YTRepliesRateLimitsCard({ channelSlot = "channel1", siloId = null, pairMode = false }) {
  const slot = SLOT_META[channelSlot] || SLOT_META.channel1;
  const [collapsed, setCollapsed] = useState(true); // starts collapsed — power-user tuning
  const [values, setValues] = useState(DEFAULTS);
  const [savedFlash, setSavedFlash] = useState(null); // which key flashed "Saved ✓" most recently
  const { owns } = useEntitlements();
  const isUnlocked = owns("yt_ai_replies");
  // Keeps the latest values inside setInterval / timeouts without stale closures.
  const savedFlashTimerRef = useRef(null);

  useEffect(() => {
    if (!isUnlocked || !siloId) return;
    ytApi.getIntegration(siloId, channelSlot).then((row) => {
      if (!row) return;
      setValues({
        maxPostsPerDay: row.max_posts_per_day ?? DEFAULTS.maxPostsPerDay,
        maxPostsPerComment: row.max_posts_per_comment ?? DEFAULTS.maxPostsPerComment,
        minSpacingSeconds: row.min_spacing_seconds ?? DEFAULTS.minSpacingSeconds,
      });
    }).catch(() => {});
  }, [isUnlocked, siloId, channelSlot]);

  const setVal = (k, v) => {
    const b = BOUNDS[k];
    const clamped = Math.max(b.min, Math.min(b.max, Number.isFinite(+v) ? +v : b.min));
    setValues((s) => ({ ...s, [k]: clamped }));
  };

  // Feb 2026 (Randy pt-yt-autosave) — fires on blur / mouseup / touchend.
  // Sends ONE patch containing all three fields (backend accepts them
  // together so mid-save reads always see a consistent snapshot).
  const persistOne = async (key) => {
    if (!isUnlocked || !siloId) return;
    try {
      const patchField = PATCH_KEYS[key];
      const val = values[key];
      await ytApi.patchIntegration(siloId, channelSlot, { [patchField]: val });
      setSavedFlash(key);
      if (savedFlashTimerRef.current) clearTimeout(savedFlashTimerRef.current);
      savedFlashTimerRef.current = setTimeout(() => setSavedFlash(null), 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed — connect YouTube first if you haven't yet.");
    }
  };

  const restore = async () => {
    setValues(DEFAULTS);
    if (!isUnlocked || !siloId) return;
    try {
      await ytApi.patchIntegration(siloId, channelSlot, {
        max_posts_per_day: DEFAULTS.maxPostsPerDay,
        max_posts_per_comment: DEFAULTS.maxPostsPerComment,
        min_spacing_seconds: DEFAULTS.minSpacingSeconds,
      });
      toast.success("Restored recommended defaults.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Restore failed.");
    }
  };

  return (
    <div
      className={pairMode ? "" : "mt-3 border hairline rounded-2xl bg-[#FDFCF8] shadow-sm overflow-hidden"}
      data-testid={`yt-rate-limits-card-${channelSlot}`}
    >
      {/* Header — HIDDEN in pairMode (the pair wrapper owns the shared header + collapse). */}
      {!pairMode && (
      <div className="px-5 py-3 border-b hairline flex items-center justify-between gap-3 bg-white/40">
        <div className="flex items-center gap-2 min-w-0">
          <Gauge size={16} weight="duotone" className="text-[#4A5D4E]" />
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#6B6A68] font-medium">Rate Limits · {slot.name}</div>
            <div className="text-[13px] font-medium text-[#1C1B1A]">Auto-post thresholds for this channel</div>
          </div>
        </div>
        <button onClick={() => setCollapsed((v) => !v)} data-testid={`yt-rate-limits-collapse-${channelSlot}`}
          className="text-[10px] uppercase tracking-[0.16em] text-[#4A5D4E] hover:text-[#1C1B1A] px-2 py-1">
          {collapsed ? "Expand ▾" : "Collapse ▴"}
        </button>
      </div>
      )}

      {(pairMode || !collapsed) && (
        <div className="p-5 space-y-4">
          {/* Preview banner — only while locked */}
          {!isUnlocked && (
            <div className="bg-[#FDF4EE]/60 border border-[#EBC9C9] rounded-md p-3 text-[12px] text-[#8A4844] flex items-start gap-2">
              <LockKey size={13} weight="duotone" className="mt-0.5 shrink-0" />
              <span>Preview mode — values are editable here for later, but won&apos;t take effect until the add-on is unlocked and your channel is connected.</span>
            </div>
          )}

          <p className="text-[12px] text-[#6B6A68] leading-relaxed flex items-start gap-2">
            <Info size={12} weight="duotone" className="mt-0.5 shrink-0 text-[#2C6E92]" />
            <span>These caps apply <strong>only when Auto-post modes are enabled</strong> on this channel&apos;s Safety-mode setting. Draft-only mode is unaffected — you approve everything by hand there anyway. Bounds: {BOUNDS.maxPostsPerDay.min}–{BOUNDS.maxPostsPerDay.max} posts/day · {BOUNDS.maxPostsPerComment.min}–{BOUNDS.maxPostsPerComment.max} per comment · {BOUNDS.minSpacingSeconds.min}–{BOUNDS.minSpacingSeconds.max}s min spacing.</span>
          </p>

          {/* Rate-limit fields */}
          <div className="space-y-3">
            <Field
              testId={`yt-rate-max-day-${channelSlot}`}
              fieldKey="maxPostsPerDay"
              label="Max posts per day"
              hint="Ceiling on how many replies auto-mode can post from this channel in any 24-hour window."
              value={values.maxPostsPerDay}
              bounds={BOUNDS.maxPostsPerDay}
              suffix={BOUNDS.maxPostsPerDay.unit}
              onChange={(v) => setVal("maxPostsPerDay", v)}
              onCommit={() => persistOne("maxPostsPerDay")}
              savedFlash={savedFlash === "maxPostsPerDay"}
            />
            <Field
              testId={`yt-rate-per-comment-${channelSlot}`}
              fieldKey="maxPostsPerComment"
              label="Max posts per comment"
              hint="How many times auto-mode is allowed to reply to a single fan comment. 1 is almost always the right answer — bumping to 2 or 3 only makes sense for extended threads."
              value={values.maxPostsPerComment}
              bounds={BOUNDS.maxPostsPerComment}
              suffix={BOUNDS.maxPostsPerComment.unit}
              onChange={(v) => setVal("maxPostsPerComment", v)}
              onCommit={() => persistOne("maxPostsPerComment")}
              savedFlash={savedFlash === "maxPostsPerComment"}
            />
            <Field
              testId={`yt-rate-spacing-${channelSlot}`}
              fieldKey="minSpacingSeconds"
              label="Minimum spacing between posts"
              hint="Seconds the worker waits between consecutive auto-posts. Prevents burst posting that YouTube's own spam filter would flag."
              value={values.minSpacingSeconds}
              bounds={BOUNDS.minSpacingSeconds}
              suffix={BOUNDS.minSpacingSeconds.unit}
              onChange={(v) => setVal("minSpacingSeconds", v)}
              onCommit={() => persistOne("minSpacingSeconds")}
              savedFlash={savedFlash === "minSpacingSeconds"}
            />
          </div>

          {/* Feb 2026 (Randy pt-yt-autosave) — Save button removed. Every
              field auto-persists on blur / slider-release. The Restore
              defaults link still lives here for quick reset. */}
          <div className="border-t hairline pt-3 flex items-center justify-between gap-3 flex-wrap">
            <button onClick={restore} data-testid={`yt-rate-restore-${channelSlot}`}
              className="text-[11.5px] text-[#6B6A68] hover:text-[#1C1B1A] inline-flex items-center gap-1">
              <ArrowClockwise size={11} weight="bold" /> Restore recommended defaults
            </button>
            <span className="text-[10.5px] text-[#6B6A68] italic inline-flex items-center gap-1.5">
              <YoutubeLogo size={11} weight="fill" className="text-[#B54848]" />
              Auto-saves on change · recommended {DEFAULTS.maxPostsPerDay} / {DEFAULTS.maxPostsPerComment} / {DEFAULTS.minSpacingSeconds}s
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ testId, fieldKey, label, hint, value, bounds, suffix, onChange, onCommit, savedFlash }) {
  return (
    <div className="border hairline rounded-lg bg-white p-3" data-testid={`${testId}-wrap`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="text-[12.5px] font-medium text-[#1C1B1A] flex items-center gap-1.5">
            {label}
            {savedFlash && (
              <span
                data-testid={`${testId}-saved-flash`}
                className="text-[10px] text-[#2F6B3A] inline-flex items-center gap-0.5 animate-in fade-in duration-150"
              >
                <CheckCircle size={10} weight="fill" /> Saved
              </span>
            )}
          </div>
          <div className="text-[11px] text-[#6B6A68] mt-0.5 leading-relaxed">{hint}</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <input
            type="number"
            min={bounds.min}
            max={bounds.max}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onCommit}
            data-testid={testId}
            className="w-20 bg-white border hairline rounded-md px-2.5 py-1.5 text-[13px] text-right focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
          />
          <span className="text-[10.5px] uppercase tracking-[0.14em] text-[#6B6A68] whitespace-nowrap">{suffix}</span>
        </div>
      </div>
      <input
        type="range"
        min={bounds.min}
        max={bounds.max}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onMouseUp={onCommit}
        onTouchEnd={onCommit}
        onKeyUp={onCommit}
        data-testid={`${testId}-slider`}
        className="w-full mt-2.5 accent-[#4A5D4E]"
      />
    </div>
  );
}
